import React, { cloneElement, useState, Fragment, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { setWizardStep } from '../../../actions/wizard';
import { FormattedMessage } from 'react-intl';
import { Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { tokens } from '@companycam/slab';

import ProgressBar from './ProgressBar';
import { Button } from '@companycam/slab-web';

const WizardFooter = styled.div`
  position: sticky;
  bottom: 0;
  left: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: calc(100% + 64px);
  padding: 16px;
  padding-left: var(--cc_size_spacing_xl);
  margin: auto 0 -32px;
  margin-left: -32px;
  background: ${tokens.color_background_2_le};
`;

const StepTitle = styled.div`
  display: flex;
  align-items: center;
  margin: 40px 0px 12px 0px;

  h2 {
    margin-bottom: 0;
    color: ${tokens.color_text_default_le};
    font-weight: 600;
    font-size: 28px;
    line-height: 1;
  }
`;

const StepNumber = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 32px;
  height: 32px;
  margin-right: 12px;
  border: 2px solid ${tokens.color_text_default_le};
  border-radius: 50%;
  color: ${tokens.color_text_default_le};
  font-weight: 600;
  font-size: 18px;
`;

const StepDescription = styled.div`
  max-width: 56em;
  margin-bottom: 48px;
  color: ${tokens.color_text_default_le};
  font-size: 18px;
  line-height: 1.5;

  &:empty {
    display: none;
  }
`;

const PhotoCount = styled.div`
  margin-right: 16px;
  color: ${tokens.color_text_default_le};
  font-size: 14px;
  font-variant-numeric: tabular-nums;
`;

/**
 * Renders a form `<Wizard />` component
 * @param  onSubmit - function that is triggered on form submission with Formik `values`
 * @param  children - steps to pass into the wizard to be rendered
 * @param  initialValues - initial state for Formik values
 * @param  initialErrors - initial state for Formik errors
 * @param  isConfirmShowing - state to keep track if `<CloseConfirm />` is visible or not
 * @param  submitStep - used as the submit button for the form
 * @param  submitLoading - used for loading state of submit button for the form
 * @param  progressBarLabels - labels for the progressbar in the footer of the wizard
 * @param  isEditing - boolean to determine if Wizard is being edited
 */
const Wizard = ({
  children,
  initialValues,
  initialErrors,
  onSubmit,
  isConfirmShowing,
  submitStep,
  submitLoading,
  progressBarLabels,
  isEditing,
  hideSelectedCount,
}) => {
  const [stepNumber, setStepNumber] = useState(0);
  const steps = React.Children.toArray(children);
  const [snapshot, setSnapshot] = useState(initialValues);
  const dispatch = useDispatch();

  const step = steps[stepNumber];
  const totalSteps = steps.length;
  const isLastStep = stepNumber === totalSteps - 1;
  const progressBarStepNumber = stepNumber + 1;

  const next = (formik) => {
    setSnapshot(formik.values);
    setStepNumber(Math.min(stepNumber + 1, totalSteps - 1));
  };

  const previous = (formik) => {
    setSnapshot(formik.values);
    setStepNumber(Math.max(stepNumber - 1, 0));
  };

  const handleSubmit = async (values, bag) => {
    if (step.props.onSubmit) {
      await step.props.onSubmit(values, bag);
    }
    if (isLastStep) {
      return onSubmit(values, bag);
    } else {
      bag.setTouched({});
      return next(bag);
    }
  };

  const {
    heading,
    description,
    nextStepName,
    errorMessage,
    eventName,
    selectedItems,
  } = step.props;

  useEffect(() => {
    dispatch(setWizardStep(eventName));
  }, [dispatch, eventName]);

  return (
    <Formik
      initialValues={snapshot}
      onSubmit={handleSubmit}
      validationSchema={step.props.validationSchema}
      initialErrors={initialErrors}
      validateOnMount={isEditing}
    >
      {(formik) => {
        const {
          values: { photos },
          isValid,
        } = formik;
        const selectionCount = selectedItems
          ? selectedItems.length
          : photos.length;
        return (
          <Form>
            {!isConfirmShowing && (
              <Fragment>
                <StepTitle>
                  <StepNumber>{progressBarStepNumber}</StepNumber>
                  <h2 data-testid="wizard-heading">{heading}</h2>
                </StepTitle>
                <StepDescription>{description}</StepDescription>
                {cloneElement(step, { formik })}
                <WizardFooter>
                  <ProgressBar
                    totalSteps={totalSteps}
                    labels={progressBarLabels}
                    currentStep={stepNumber + 1}
                  />
                  {stepNumber > 0 ? (
                    <Button onClick={() => previous(formik)}>
                      <FormattedMessage
                        id="Core.forms.wizard.previousStepButton"
                        defaultMessage="Previous Step"
                        description="Label for Wizard Previous Step Button"
                      />
                    </Button>
                  ) : (
                    !hideSelectedCount && (
                      <PhotoCount>
                        <FormattedMessage
                          id="Core.forms.wizard.photoCount"
                          defaultMessage="{photoCount, plural, one {# selected} other {# selected}}"
                          description="Label for Wizard Photo Count"
                          values={{
                            photoCount: selectionCount,
                          }}
                        />
                      </PhotoCount>
                    )
                  )}
                  <div>
                    <Button
                      color="primary"
                      style={{ marginBottom: 0, marginLeft: 16 }}
                      className={!formik.isValid ? 'hint hint--top-center' : ''}
                      disabled={!isValid || submitLoading}
                      type="submit"
                      aria-label={!isValid && `${errorMessage}`}
                    >
                      {isLastStep ? submitStep : nextStepName}
                    </Button>
                  </div>
                </WizardFooter>
              </Fragment>
            )}
          </Form>
        );
      }}
    </Formik>
  );
};

Wizard.propTypes = {
  /** steps to pass into the wizard to be rendered */
  children: PropTypes.arrayOf(PropTypes.element).isRequired,
  /** initial state for Formik values */
  initialValues: PropTypes.object.isRequired,
  /** initial state for Formik errors */
  initialErrors: PropTypes.object,
  /** function that is triggered on form submission with Formik `values` */
  onSubmit: PropTypes.func.isRequired,
  /** state to keep track if `<CloseConfirm />` is visible or not */
  isConfirmShowing: PropTypes.bool,
  /** used as the submit button for the form  */
  submitStep: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  /** used for loading state of submit button for the form */
  submitLoading: PropTypes.bool,
  /** labels for the progressbar in the footer of the wizard */
  progressBarLabels: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
  ),
  /** boolean to determine if Wizard is being edited */
  isEditing: PropTypes.bool,
  /** boolean to determine if selected count should be hidden */
  hideSelectedCount: PropTypes.bool,
};

export default Wizard;
